import React from "react";
import { Link } from "react-router-dom";
import { Award, Mail, ExternalLink, FileText, ShieldCheck, Phone } from "lucide-react";
import footerImage from "../../../../assets/footer_image.webp";
import FooterEasterEgg from "./FooterEasterEgg";

interface FooterProps {
  variant?: "full" | "short" | "auto";
}

export default function Footer({ variant = "auto" }: FooterProps = {}) {
  const [showStack, setShowStack] = React.useState(false);
  const [currentPath, setCurrentPath] = React.useState(window.location.pathname);

  React.useEffect(() => {
    const handleRoute = () => {
      setCurrentPath(window.location.pathname);
    };
    window.addEventListener("popstate", handleRoute);
    return () => window.removeEventListener("popstate", handleRoute);
  }, []);

  const cleanPath = currentPath.split("?")[0].replace(/\/$/, "");
  const isFullFooter = variant === "full" || (variant === "auto" && (!cleanPath || cleanPath === "/" || cleanPath === "/advisory"));

  const menuLinks = [
    { label: "CAIO Diagnostic", href: "/caio-diagnostic" },
    { label: "Fractional CAIO", href: "/" },
    { label: "Strategic Advisory", href: "/strategic-advisory" },
    { label: "Legal AI Systems", href: "/legal" },
    { label: "Construction AI", href: "/construction" },
    { label: "Industrial R&D", href: "/industrial" },
    { label: "Insights", href: "/insights" },
    { label: "Project Portfolio", href: "/portfolio" },
    { label: "Credentials Vault", href: "/credentials" },
    { label: "Executive Search", href: "/executive-search" },
    { label: "Intake Protocol", href: "#intake-form" }
  ];

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith("/")) {
      // Let global App interceptor handle it
      return;
    }

    const isHome = window.location.pathname === "/";
    if (!isHome) {
      e.preventDefault();
      sessionStorage.setItem("scrollToSection", href);
      window.history.pushState({}, "", "/");
      window.dispatchEvent(new PopStateEvent("popstate"));
    } else {
      e.preventDefault();
      const target = document.querySelector(href);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  if (!isFullFooter) {
    return (
      <footer className="border-t-4 border-brand-burgundy bg-brand-charcoal text-brand-sand py-8 font-sans relative overflow-hidden">
        {/* Background ambient warmth */}
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-brand-orange/[0.01] rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-6xl mx-auto px-6 space-y-6">
          {/* Top Row: 3 Zones */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Left Zone: Identity Anchor */}
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-sm bg-brand-burgundy flex items-center justify-center font-serif text-sm font-bold text-brand-sand select-none shadow-inner">
                C
              </span>
              <span className="text-xs font-mono font-bold tracking-widest uppercase text-white">
                CRISPIN COURTENAY
              </span>
            </div>

            {/* Center Zone: Tagline Strip */}
            <div className="flex flex-wrap justify-center gap-3 text-[9px] font-bold uppercase tracking-wider text-brand-orange/80 text-center">
              <span>SECURE-BY-DESIGN</span>
              <span>•</span>
              <span>ZERO-TRUST NATIVE</span>
              <span>•</span>
              <span>DETERMINISTIC WORKFLOWS</span>
              <span>•</span>
              <span>AGENTIC SCAFFOLDING</span>
            </div>

            {/* Right Zone: California Graphic (scaled down to ~60% - max-[90px] wide) */}
            <div className="flex justify-center md:justify-end">
              <a 
                href="https://www.tahoedailytribune.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="max-w-[90px] block rounded overflow-hidden shadow-md border border-brand-sand/15 bg-brand-charcoal p-1 transition-all hover:scale-102 hover:border-brand-orange/60 cursor-pointer"
              >
                <img 
                  src={footerImage} 
                  alt="California Republic Insignia" 
                  className="w-full h-auto object-contain select-none"
                  width={90}
                  height={90}
                  loading="lazy"
                />
              </a>
            </div>
          </div>

          {/* Bottom Row: Thin Secondary Row */}
          <div className="pt-6 border-t border-brand-espresso flex flex-col md:flex-row items-center justify-between gap-4 text-[10px] text-brand-sand/70 font-mono tracking-wide">
            <div>
              &copy; 2026 CRISPIN COURTENAY. ALL RIGHTS RESERVED.
            </div>
            
            <div className="flex flex-wrap gap-x-4 gap-y-2 justify-center md:justify-end text-brand-sand/70 font-light">
              <Link to="/" className="hover:text-brand-orange transition-colors">Fractional CAIO</Link>
              <span>·</span>
              <Link to="/strategic-advisory" className="hover:text-brand-orange transition-colors">Strategic Advisory</Link>
              <span>·</span>
              <Link to="/legal" className="hover:text-brand-orange transition-colors">Legal AI</Link>
              <span>·</span>
              <Link to="/construction" className="hover:text-brand-orange transition-colors">Construction AI</Link>
              <span>·</span>
              <Link to="/industrial" className="hover:text-brand-orange transition-colors">Industrial R&D</Link>
              <span>·</span>
              <Link to="/portfolio" className="hover:text-brand-orange transition-colors">Portfolio</Link>
              <span>·</span>
              <Link to="/insights" className="hover:text-brand-orange transition-colors">Insights</Link>
              <span>·</span>
              <Link to="/credentials" className="hover:text-brand-orange transition-colors">Credentials Vault</Link>
              <span>·</span>
              <Link to="/executive-search" className="hover:text-brand-orange transition-colors">Executive Search</Link>
              <span>·</span>
              <a href="#intake-form" className="hover:text-brand-orange transition-colors">Intake Protocol</a>
            </div>
          </div>

          <FooterEasterEgg />
        </div>
      </footer>
    );
  }

  return (
    <footer className="border-t-4 border-brand-burgundy bg-brand-charcoal text-brand-sand py-16 font-sans relative overflow-hidden">
      
      {/* Background ambient warmth */}
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-brand-orange/[0.01] rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-6xl mx-auto px-6">
        
        {/* Main Columns Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 items-start mb-16">
          
          {/* Column 1: Habit-Style Menu Links with Dashed Orange Underlines */}
          <div className="flex flex-col space-y-1">
            <span className="text-[10px] font-mono uppercase tracking-widest text-brand-orange font-bold block mb-4">
              Practice
            </span>
            {menuLinks.map((link) => (
              link.href.startsWith("#") ? (
                <a 
                  key={link.label}
                  href={link.href} 
                  className="group py-2.5 border-b border-dashed border-brand-sand/20 hover:border-brand-orange flex items-center justify-between transition-colors cursor-pointer"
                >
                  <span className="text-sm font-semibold group-hover:text-white transition-colors">
                    {link.label}
                  </span>
                  <span className="text-xs font-mono text-brand-orange opacity-60 group-hover:opacity-100 transition-opacity">
                    →
                  </span>
                </a>
              ) : (
                <Link 
                  key={link.label}
                  to={link.href} 
                  className="group py-2.5 border-b border-dashed border-brand-sand/20 hover:border-brand-orange flex items-center justify-between transition-colors cursor-pointer"
                >
                  <span className="text-sm font-semibold group-hover:text-white transition-colors">
                    {link.label}
                  </span>
                  <span className="text-xs font-mono text-brand-orange opacity-60 group-hover:opacity-100 transition-opacity">
                    →
                  </span>
                </Link>
              )
            ))}
          </div>

          {/* Column 2: Download Strategic Briefings */}
          <div className="flex flex-col space-y-5">
            <span className="text-[10px] font-mono uppercase tracking-widest text-brand-orange font-bold block">
              Engagement Documents
            </span>
            
            <p className="text-xs text-brand-sand/70 leading-relaxed font-light">
              Review compiled guidelines on cryptographic data control and zero-trust local network configurations.
            </p>

            <div className="space-y-3 pt-2">
              <a 
                href="/Downloads/Documents/FiduciaryBrief"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-brand-espresso border border-brand-sand/10 hover:border-brand-orange rounded-sm transition-all group"
              >
                <div className="w-8 h-8 rounded-sm bg-brand-burgundy/40 text-brand-orange flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-bold text-white block leading-tight group-hover:text-brand-orange transition-colors">
                    Fiduciary Brief
                  </span>
                  <span className="text-[9px] font-mono text-brand-sand/70 block mt-0.5 uppercase">
                    Fractional CAIO Structure
                  </span>
                </div>
              </a>

              <a 
                href="/Downloads/Documents/MasterEngagementAgreement"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-brand-espresso border border-brand-sand/10 hover:border-brand-orange rounded-sm transition-all group"
              >
                <div className="w-8 h-8 rounded-sm bg-brand-burgundy/40 text-brand-orange flex items-center justify-center shrink-0">
                  <FileText className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-bold text-white block leading-tight group-hover:text-brand-orange transition-colors">
                    Master Engagement Agreement
                  </span>
                  <span className="text-[9px] font-mono text-brand-sand/70 block mt-0.5 uppercase">
                    Solopreneur Executive Terms
                  </span>
                </div>
              </a>

              <a 
                href="/Downloads/Documents/BoardAdvisoryAgreement"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-brand-espresso border border-brand-sand/10 hover:border-brand-orange rounded-sm transition-all group"
              >
                <div className="w-8 h-8 rounded-sm bg-brand-burgundy/40 text-brand-orange flex items-center justify-center shrink-0">
                  <FileText className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-bold text-white block leading-tight group-hover:text-brand-orange transition-colors">
                    Board Advisory Agreement
                  </span>
                  <span className="text-[9px] font-mono text-brand-sand/70 block mt-0.5 uppercase">
                    FAST-Hybrid Framework
                  </span>
                </div>
              </a>
            </div>
          </div>

          {/* Column 3: Governance Alignment */}
          <div className="flex flex-col space-y-4 lg:pl-4 text-left">
            <span className="text-[10px] font-mono uppercase tracking-widest text-brand-orange font-bold block">
              Governance Alignment
            </span>
            <p className="text-xs text-brand-sand/70 leading-relaxed font-light">
              Advisory work can support ISO/IEC 42001-aligned AI management systems, NIST AI RMF mapping, audit evidence, lifecycle controls, and executive accountability.
            </p>
            <p className="text-[11px] text-brand-sand/70 font-mono tracking-wide leading-relaxed border-t border-brand-espresso pt-4">
              Formal engagement terms govern scope, confidentiality, access, and delivery.
            </p>
          </div>

          {/* Column 4: California Bear Badge */}
          <div className="flex justify-center lg:justify-end mt-6 lg:mt-0">
            <a 
              href="https://www.tahoedailytribune.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="max-w-[155px] block rounded overflow-hidden shadow-xl border border-brand-sand/15 bg-brand-charcoal p-1 transition-all hover:scale-102 hover:border-brand-orange/60 cursor-pointer"
            >
              <img 
                src={footerImage} 
                alt="California Republic Insignia" 
                className="w-full h-auto object-contain select-none"
                width={155}
                height={155}
                loading="lazy"
              />
            </a>
          </div>

        </div>

        {/* Collapsible System Architecture Block */}
        <div className="border-t border-brand-espresso pt-8 mb-8">
          <button
            onClick={() => setShowStack(!showStack)}
            className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-brand-orange hover:text-white font-bold transition-colors focus:outline-none cursor-pointer group"
          >
            <span className="text-brand-orange group-hover:text-white transition-colors">
              {showStack ? "[-] Hide System Architecture Scaffold" : "[+] View System Architecture Scaffold (Technical Materials)"}
            </span>
          </button>

          {showStack && (
            <div className="mt-4 p-5 bg-brand-espresso/40 border border-brand-sand/10 rounded-sm grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 animate-fadeIn">
              <div className="space-y-1.5">
                <h4 className="text-xs font-bold text-white uppercase tracking-wider font-mono">Frontend Runtime</h4>
                <p className="text-xs text-brand-sand/70 font-light leading-relaxed">
                  React 19 &amp; TypeScript compiled via Vite. Deployed globally onto Cloudflare Edge Networks with HSTS, strict CSP controls, and Cloudflare Web Analytics telemetry.
                </p>
              </div>
              <div className="space-y-1.5">
                <h4 className="text-xs font-bold text-white uppercase tracking-wider font-mono">Backend Orchestrator</h4>
                <p className="text-xs text-brand-sand/70 font-light leading-relaxed">
                  Rust API (Axum REST / Tonic gRPC) containerized via Docker and executed on GCP Cloud Run behind an NGINX ingress proxy with Redis-Alpine caching.
                </p>
              </div>
              <div className="space-y-1.5">
                <h4 className="text-xs font-bold text-white uppercase tracking-wider font-mono">Database &amp; Storage</h4>
                <p className="text-xs text-brand-sand/70 font-light leading-relaxed">
                  Production SQL hosted on managed Supabase (PostgreSQL 17) with pgvector, synced via SQLx. Backed by automated rolling local pg_dumps and a Neo4j Enterprise Graph cluster.
                </p>
              </div>
              <div className="space-y-1.5">
                <h4 className="text-xs font-bold text-white uppercase tracking-wider font-mono">Loki Workflow</h4>
                <p className="text-xs text-brand-sand/70 font-light leading-relaxed">
                  Grafana alerts stream container logs to Loki. A custom Rust parser ingests and normalizes logs, routing duplicates and promoting jules-approved errors via GitHub Actions and GitHub Projects to Gemini Jules, subject to a strict 150-issue/day cap for autonomous patch generation.
                </p>
              </div>
              <div className="space-y-1.5">
                <h4 className="text-xs font-bold text-white uppercase tracking-wider font-mono">Architectural Rationale</h4>
                <p className="text-xs text-brand-sand/70 font-light leading-relaxed italic">
                  "I was flexing a bit, and did not want to redesign it 6 months from now." Instrumented via Prometheus &amp; Grafana dashboards to eliminate maintenance overhead.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Bottom copyright & legal compliance line */}
        <div className="pt-10 border-t border-brand-espresso flex flex-col items-start gap-6 text-xs text-brand-sand/70 font-mono tracking-wide">
          <div className="text-left space-y-4 w-full">
            <div className="flex flex-wrap items-center justify-between gap-4 w-full border-b border-brand-espresso/60 pb-4">
              <p className="font-bold text-brand-sand/70 uppercase">
                &copy; 2026 CRISPIN COURTENAY. ALL RIGHTS RESERVED.
              </p>
              <a 
                href="https://www.linkedin.com/in/crispincourtenay/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-xs text-brand-sand/70 hover:text-brand-orange transition-colors flex items-center gap-1 font-mono uppercase tracking-wider font-bold"
              >
                LinkedIn <ExternalLink className="w-3 h-3 text-brand-orange" />
              </a>
            </div>
            
            <div className="space-y-1">
              <p className="max-w-3xl text-[10px] md:text-xs text-brand-sand/70 font-sans font-light leading-relaxed">
                Systems are designed to support strict American enterprise governance standards, California regulations, emerging New York AI legislation, Canada’s PIPEDA framework, voluntary AI code guidance, SOC 2, ISO 42001, and NIST AI RMF 1.0 where applicable. Final compliance obligations depend on client scope, jurisdiction, and executed engagement terms.
              </p>
            </div>
            <div className="flex flex-wrap gap-4 text-xs md:text-sm font-bold uppercase tracking-wider text-[#FF7A30]">
              <span>SECURE-BY-DESIGN</span>
              <span>•</span>
              <span>ZERO-TRUST NATIVE</span>
              <span>•</span>
              <span>DETERMINISTIC WORKFLOWS</span>
              <span>•</span>
              <span>AGENTIC SCAFFOLDING</span>
            </div>
          </div>
        </div>

        <FooterEasterEgg />
      </div>
    </footer>
  );
}
